import { Head, Link, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import DateRangeFilter, { DateRange, getDateLabel } from '@/components/DateRangeFilter'

interface PoolRow {
  agent_id: number
  agent_name: string
  agent_type: string
  sponsor_name: string | null
  total_credits: number
  percentage: number | null
}

interface Summary {
  org_total: number
  agents_with_credits: number
}

interface Props extends PageProps {
  rows: PoolRow[]
  summary: Summary
  date_range: DateRange
}

const AGENT_TYPE_LABELS: Record<string, string> = {
  writing_agent: 'Writing Agent',
  sub_agent: 'Sponsored Agent'
}

export default function EquityPool({ rows, summary, date_range }: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'

  const periodLabel = date_range.preset === 'all_time' ? '' : ` — ${getDateLabel(date_range)}`
  const orgTotalIsZero = summary.org_total === 0

  const formatCredits = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const columns: Column<PoolRow>[] = [
    {
      key: 'agent_name',
      header: 'Agent',
      sortable: true,
      render: (row) => (
        <Link
          href={`/agents/${row.agent_id}`}
          className="font-medium text-chestnut-forest hover:text-chestnut-gold"
        >
          {row.agent_name}
        </Link>
      )
    },
    {
      key: 'agent_type',
      header: 'Agent Type',
      sortable: true,
      render: (row) => AGENT_TYPE_LABELS[row.agent_type] ?? row.agent_type
    },
    {
      key: 'sponsor_name',
      header: 'Sponsor',
      sortable: true,
      className: 'text-chestnut-charcoal/70',
      render: (row) => row.sponsor_name || '—'
    },
    {
      key: 'total_credits',
      header: 'Total Credits',
      sortable: true,
      className: 'text-right font-semibold text-chestnut-forest',
      headerClassName: 'text-right',
      render: (row) => formatCredits(row.total_credits)
    },
    {
      key: 'percentage',
      header: 'Percentage of Pool',
      sortable: true,
      className: 'text-right font-semibold text-chestnut-charcoal',
      headerClassName: 'text-right',
      render: (row) =>
        orgTotalIsZero || row.percentage === null
          ? 'N/A'
          : `${row.percentage.toFixed(2)}%`
    }
  ]

  return (
    <AppLayout>
      <Head title="Equity Pool Summary" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Equity Pool Summary</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Each agent's share of the total equity pool</p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter dateRange={date_range} path="/equity_credits/pool" />
          </div>
        </div>

        {isAdmin && (
          <div className="flex gap-2 border-b border-chestnut-gray">
            <Link
              href="/equity_credits"
              className="px-4 py-2 text-sm font-medium text-chestnut-charcoal/70 hover:text-chestnut-forest"
            >
              Credits
            </Link>
            <Link
              href="/equity_credits/pool"
              className="px-4 py-2 text-sm font-medium border-b-2 border-chestnut-forest text-chestnut-forest"
            >
              Pool Summary
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card border-l-4 border-l-chestnut-gold">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Total Pool Credits{periodLabel}</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{formatCredits(summary.org_total)}</p>
          </div>
          <div className="card border-l-4 border-l-chestnut-forest">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Agents with Credits</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.agents_with_credits}</p>
          </div>
        </div>

        <div className="card p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search agents..."
            searchKeys={['agent_name', 'sponsor_name']}
            pageSize={25}
            emptyMessage="No equity credits in this period."
            getRowKey={(row) => row.agent_id}
          />
        </div>
      </div>
    </AppLayout>
  )
}
